/**
 * RecorderPopup
 *
 * Standalone recording page opened as a popup window from the Halo PSA iframe.
 * Handles the full flow: consent → record → stop → upload → process → notes.
 * Communicates results back to the parent iframe via postMessage.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Mic, Square, Pause, Play, X, AlertCircle,
  Loader2, CheckCircle2, Upload, Clock, Check, PhoneCall
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useCallRecordingContext, formatDuration } from '@/contexts/CallRecordingContext'
import type { AudioCaptureMode } from '@/contexts/CallRecordingContext'
import { useRecordingFlow, useRecordingStatus, useMidCallUpdate, retryProcessing } from '@/hooks/useCallRecordings'
import { getAudioFormat } from '@/hooks/useCallRecording'
import { useAudioDevices } from '@/hooks/useAudioDevices'
import { CallNotesEditor } from '@/components/calls/CallNotesEditor'
import { hasValidSession, injectSessionToken } from '@/services/api'

export function RecorderPopup() {
  const params = new URLSearchParams(window.location.search)
  const initialTicketId = params.get('ticketId') ? parseInt(params.get('ticketId')!, 10) : undefined
  const ticketSummary = params.get('ticketSummary') || undefined
  const theme = params.get('theme') || 'dark'

  // Apply dark class to document for popup window (default to dark)
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [theme])

  const ticketId = initialTicketId
  const [phase, setPhase] = useState<'select' | 'recording' | 'processing' | 'notes' | 'done'>('select')
  const [sessionReady, setSessionReady] = useState(false)
  const [captureMode, setCaptureMode] = useState<AudioCaptureMode>('microphone')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [uploadStarted, setUploadStarted] = useState(false)
  const [showDevicePicker, setShowDevicePicker] = useState(false)
  const [selectedSpeakerDeviceId, setSelectedSpeakerDeviceId] = useState<string | undefined>(undefined)
  const [showScreenShareGuide, setShowScreenShareGuide] = useState(false)

  const {
    isPaused, duration, audioLevel, status, error: contextError,
    audioBlob, whisperBlob, recordingId,
    startRecording, stopRecording, pauseRecording, resumeRecording, cancelRecording,
    extractPartialAudio,
    setRecordingId, setStatus, setError,
    isMicrophoneSupported,
    recoverableSession, recoverSession, dismissRecovery,
  } = useCallRecordingContext()

  const { devices, loopbackDevice, hasLoopback, hasVirtualCable } = useAudioDevices()

  const { uploadAndProcessMultipart } = useRecordingFlow()

  // Try to get session token from opener window; fall through to API key auth if unavailable
  useEffect(() => {
    if (hasValidSession()) {
      setSessionReady(true)
      return
    }
    if (!window.opener) {
      // No opener — proceed with API key auth (halopod supports API key fallback)
      console.log('[RecorderPopup] No opener window — proceeding with API key auth')
      setSessionReady(true)
      return
    }

    const handleTokenResponse = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      if (event.data?.type !== 'halopod-session-response') return
      window.removeEventListener('message', handleTokenResponse)
      clearTimeout(timeout)

      if (event.data.token && event.data.expiresAt) {
        injectSessionToken(event.data.token, event.data.expiresAt)
        console.log('[RecorderPopup] Session token received from opener')
      } else {
        console.log('[RecorderPopup] No session token from opener — using API key auth')
      }
      setSessionReady(true)
    }
    window.addEventListener('message', handleTokenResponse)
    window.opener.postMessage({ type: 'halopod-session-request' }, window.location.origin)

    // If parent doesn't respond, fall through — API key auth will handle it
    const timeout = setTimeout(() => {
      window.removeEventListener('message', handleTokenResponse)
      console.log('[RecorderPopup] Session handshake timed out — proceeding with API key auth')
      setSessionReady(true)
    }, 3000)

    return () => {
      window.removeEventListener('message', handleTokenResponse)
      clearTimeout(timeout)
    }
  }, [])

  // Mid-call update state
  const [autoUpdateMinutes, setAutoUpdateMinutes] = useState<number | null>(null)
  const autoUpdateTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const {
    triggerUpdate: midCallTriggerUpdate,
    phase: midCallPhase,
    message: midCallMessage,
    updateCount: midCallUpdateCount,
    lastUpdateTime: midCallLastUpdateTime,
    isActive: midCallIsActive,
  } = useMidCallUpdate(ticketId)

  // Auto-select TRUE loopback device (Stereo Mix) when twoway is chosen
  // Virtual cables (VB-Cable) are NOT auto-selected — they break live calls
  useEffect(() => {
    if (captureMode === 'twoway' && loopbackDevice) {
      setSelectedSpeakerDeviceId(loopbackDevice.deviceId)
    } else if (captureMode === 'twoway') {
      setSelectedSpeakerDeviceId(undefined) // Will use getDisplayMedia (recommended)
    }
  }, [captureMode, loopbackDevice])

  const handleStart = async () => {
    try {
      setErrorMessage(null)
      const speakerId = captureMode === 'twoway' ? selectedSpeakerDeviceId : undefined

      // Show screen share guide for two-way without loopback (first time only)
      const guideKey = 'halopod-screenshare-guide-dismissed'
      if (captureMode === 'twoway' && !speakerId && !localStorage.getItem(guideKey)) {
        setShowScreenShareGuide(true)
        // Wait for user to dismiss guide before proceeding
        await new Promise<void>((resolve) => {
          const check = setInterval(() => {
            // Guide sets showScreenShareGuide to false when dismissed
            if (!document.getElementById('screenshare-guide')) {
              clearInterval(check)
              resolve()
            }
          }, 100)
          // Auto-proceed after 10s in case element detection fails
          setTimeout(() => { clearInterval(check); resolve() }, 10000)
        })
      }

      await startRecording(captureMode, ticketId, ticketSummary, speakerId)
      setPhase('recording')
    } catch (err) {
      setShowScreenShareGuide(false)
      setErrorMessage(err instanceof Error ? err.message : 'Failed to start recording')
    }
  }

  const handleStop = async () => {
    // Clear auto-update timer before stopping
    if (autoUpdateTimerRef.current) {
      clearInterval(autoUpdateTimerRef.current)
      autoUpdateTimerRef.current = null
    }
    await stopRecording()
  }

  // Mid-call update handler
  const handleMidCallUpdate = useCallback(async () => {
    if (midCallIsActive || !ticketId) return
    const blob = await extractPartialAudio()
    if (blob) {
      await midCallTriggerUpdate(blob, duration)
    }
  }, [midCallIsActive, ticketId, extractPartialAudio, midCallTriggerUpdate, duration])

  // Stable ref for the auto-update callback so the timer doesn't reset every second
  const midCallUpdateRef = useRef(handleMidCallUpdate)
  useEffect(() => {
    midCallUpdateRef.current = handleMidCallUpdate
  }, [handleMidCallUpdate])

  // Auto-update interval management (deps exclude callback to keep timer stable)
  useEffect(() => {
    if (autoUpdateTimerRef.current) {
      clearInterval(autoUpdateTimerRef.current)
      autoUpdateTimerRef.current = null
    }

    if (autoUpdateMinutes && phase === 'recording' && !isPaused && ticketId) {
      autoUpdateTimerRef.current = setInterval(() => {
        midCallUpdateRef.current()
      }, autoUpdateMinutes * 60 * 1000)
    }

    return () => {
      if (autoUpdateTimerRef.current) {
        clearInterval(autoUpdateTimerRef.current)
        autoUpdateTimerRef.current = null
      }
    }
  }, [autoUpdateMinutes, phase, isPaused, ticketId])

  // Upload when blob is ready
  useEffect(() => {
    if (audioBlob && status === 'uploading' && !uploadStarted && !recordingId) {
      setUploadStarted(true)
      setPhase('processing')
      handleUpload()
    }
  }, [audioBlob, status, uploadStarted, recordingId])

  const handleUpload = async () => {
    if (!audioBlob) return
    try {
      const format = getAudioFormat(audioBlob.type)
      const id = await uploadAndProcessMultipart({
        audioBlob,
        whisperBlob: whisperBlob ?? undefined,
        audioFormat: format,
        durationSeconds: duration,
        ticketId,
      })
      setRecordingId(id)
      setStatus('processing')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
      setStatus('error')
    }
  }

  // Poll for processing status
  const { data: statusData } = useRecordingStatus(
    status === 'processing' && recordingId ? recordingId : null,
    { pollingInterval: 2000 }
  )

  useEffect(() => {
    if (statusData?.status === 'completed') {
      setStatus('complete')
      setPhase('notes')
    } else if (statusData?.status === 'failed') {
      setError(statusData.errorMessage || 'Processing failed')
      setStatus('error')
    }
  }, [statusData, setStatus, setError])

  // Retry processing for failed or timed-out recordings
  const [retrying, setRetrying] = useState(false)
  const handleRetryProcessing = async () => {
    if (!recordingId) return
    try {
      setRetrying(true)
      setErrorMessage(null)
      await retryProcessing(recordingId)
      setStatus('processing')
      setPhase('processing')
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Retry failed')
    } finally {
      setRetrying(false)
    }
  }

  // Show context error
  useEffect(() => {
    if (contextError) setErrorMessage(contextError)
  }, [contextError])

  // Notify parent and close
  const handleDone = useCallback(() => {
    if (window.opener) {
      window.opener.postMessage({
        type: 'halopod-recording-complete',
        recordingId,
        ticketId,
      }, '*')
    }
    window.close()
  }, [recordingId, ticketId])

  const handleCancel = () => {
    cancelRecording()
    if (window.opener) {
      window.opener.postMessage({ type: 'halopod-recording-cancelled' }, '*')
    }
    window.close()
  }

  const processingLabel = status === 'uploading'
    ? 'Uploading audio...'
    : statusData?.status === 'transcribing'
      ? 'Transcribing with AI...'
      : statusData?.status === 'generating_notes'
        ? 'Generating notes...'
        : 'Processing...'
  const processingProgress = statusData?.progress || (status === 'uploading' ? 20 : 40)

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="bg-background border-b px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-sm">H</span>
          </div>
          <div>
            <span className="text-sm font-semibold text-foreground">Call Recording</span>
            {ticketId && (
              <p className="text-xs text-muted-foreground">Ticket #{ticketId}</p>
            )}
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={handleCancel} title="Close">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {/* Select Mode Phase */}
        {phase === 'select' && (
          <div className="max-w-md mx-auto space-y-6">
            {/* Recovery prompt (WS6) */}
            {recoverableSession && (
              <div className="flex flex-col gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md text-sm">
                <div className="flex items-start gap-2 text-amber-800 dark:text-amber-300">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">Unsaved recording found</p>
                    <p className="text-xs mt-1">
                      {recoverableSession.session.ticketSummary
                        ? `"${recoverableSession.session.ticketSummary}" — `
                        : ''}
                      {Math.floor(recoverableSession.session.durationSeconds / 60)}m {recoverableSession.session.durationSeconds % 60}s recorded
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 ml-6">
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={recoverSession}>
                    Recover & Upload
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground" onClick={dismissRecovery}>
                    Discard
                  </Button>
                </div>
              </div>
            )}

            <div className="flex gap-2 p-3 bg-primary/10 rounded-md text-sm text-primary">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <p>This call will be recorded and transcribed. Make sure you have consent from all parties.</p>
            </div>

            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground">Recording Mode</label>

              <label className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer ${
                captureMode === 'microphone' ? 'border-primary bg-primary/5' : 'border-border'
              } ${!isMicrophoneSupported ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <input
                  type="radio" name="captureMode" value="microphone"
                  checked={captureMode === 'microphone'}
                  onChange={() => setCaptureMode('microphone')}
                  disabled={!isMicrophoneSupported}
                  className="mt-1"
                />
                <div>
                  <div className="flex items-center gap-2 font-medium text-sm text-foreground">
                    <Mic className="h-4 w-4" /> Microphone Only
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Captures your voice only. Use when dictating notes or customer hasn't consented.
                  </p>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer ${
                captureMode === 'twoway' ? 'border-primary bg-primary/5' : 'border-border'
              } ${!isMicrophoneSupported ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <input
                  type="radio" name="captureMode" value="twoway"
                  checked={captureMode === 'twoway'}
                  onChange={() => setCaptureMode('twoway')}
                  disabled={!isMicrophoneSupported}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 font-medium text-sm text-foreground">
                    <PhoneCall className="h-4 w-4" /> Mic + Speaker (Two-Way)
                    <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded font-normal">Recommended</span>
                  </div>
                  {hasLoopback ? (
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center gap-1">
                      <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
                      Ready — {loopbackDevice!.label} detected (zero-dialog)
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-1">
                      Captures both sides via screen sharing. Select your screen and check "Share audio".
                    </p>
                  )}
                </div>
              </label>

              {/* Two-way details: guidance + device picker */}
              {captureMode === 'twoway' && (
                <div className="ml-7 space-y-2">
                  {/* Virtual cable warning — VB-Cable alone cannot do two-way */}
                  {hasVirtualCable && !hasLoopback && (
                    <div className="flex gap-2 p-2 bg-amber-50 dark:bg-amber-950/30 rounded text-xs text-amber-700 dark:text-amber-300">
                      <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <p>
                        VB-Cable detected but it cannot capture two-way audio alone — it redirects audio
                        instead of mirroring it. Use the screen share method below (recommended) or enable
                        Stereo Mix in your sound settings for zero-dialog recording.
                      </p>
                    </div>
                  )}

                  {/* Screen share guide — shown before first use */}
                  {showScreenShareGuide && (
                    <div id="screenshare-guide" className="p-3 bg-primary/5 border border-primary/20 rounded-md text-xs space-y-2">
                      <p className="font-medium text-foreground">Quick setup (one time only):</p>
                      <ol className="list-decimal ml-4 text-muted-foreground space-y-1">
                        <li>A sharing dialog will appear</li>
                        <li>Select <strong>Entire Screen</strong> (not a window or tab)</li>
                        <li>Check <strong>"Share system audio"</strong> at the bottom</li>
                        <li>Click <strong>Share</strong></li>
                      </ol>
                      <div className="flex gap-2 mt-2">
                        <button
                          type="button"
                          className="text-xs bg-primary text-primary-foreground px-3 py-1 rounded hover:bg-primary/90"
                          onClick={() => {
                            setShowScreenShareGuide(false)
                            localStorage.setItem('halopod-screenshare-guide-dismissed', '1')
                          }}
                        >
                          Got it, continue
                        </button>
                        <button
                          type="button"
                          className="text-xs text-primary hover:underline px-2 py-1"
                          onClick={() => {
                            setShowScreenShareGuide(false)
                            localStorage.setItem('halopod-screenshare-guide-dismissed', '1')
                          }}
                        >
                          Don't show again
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Advanced: device picker for true loopback devices */}
                  <button
                    type="button"
                    className="text-xs text-primary hover:underline"
                    onClick={() => setShowDevicePicker(!showDevicePicker)}
                  >
                    {showDevicePicker ? 'Hide' : 'Advanced:'} audio device picker
                  </button>
                  {showDevicePicker && (
                    <>
                      <select
                        className="w-full text-xs border rounded p-1.5 bg-background text-foreground"
                        value={selectedSpeakerDeviceId || ''}
                        onChange={(e) => setSelectedSpeakerDeviceId(e.target.value || undefined)}
                      >
                        <option value="">Screen share (recommended)</option>
                        {devices.filter(d => d.isLoopback).map(d => (
                          <option key={d.deviceId} value={d.deviceId}>
                            {d.label} (loopback — zero dialog)
                          </option>
                        ))}
                        {devices.filter(d => d.isVirtualCable).map(d => (
                          <option key={d.deviceId} value={d.deviceId}>
                            {d.label} (virtual cable — not recommended)
                          </option>
                        ))}
                      </select>
                      {selectedSpeakerDeviceId && devices.find(d => d.deviceId === selectedSpeakerDeviceId)?.isVirtualCable && (
                        <div className="flex gap-2 p-2 bg-destructive/10 rounded text-xs text-destructive">
                          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                          <p>
                            Warning: Virtual cables redirect audio instead of mirroring it. This will
                            likely result in one-sided recording. Use "Screen share" instead.
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {errorMessage && (
              <div className="flex gap-2 p-3 bg-destructive/10 rounded-md text-sm text-destructive">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <p>{errorMessage}</p>
              </div>
            )}

            <Button
              onClick={handleStart}
              disabled={!sessionReady}
              className="w-full gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {!sessionReady ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Connecting...</>
              ) : (
                <><Mic className="h-4 w-4" /> Start Recording</>
              )}
            </Button>
          </div>
        )}

        {/* Recording Phase */}
        {phase === 'recording' && (
          <div className="max-w-md mx-auto space-y-4">
            <div className="text-center space-y-4">
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
                isPaused ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'
              }`}>
                <span className={`w-3 h-3 rounded-full ${
                  isPaused ? 'bg-yellow-500' : 'bg-red-500 animate-pulse'
                }`} />
                {isPaused ? 'Paused' : 'Recording'}
              </div>

              <div className="font-mono text-5xl font-semibold text-foreground">
                {formatDuration(duration)}
              </div>

              <div className="flex items-center justify-center gap-2">
                <Mic className={`h-5 w-5 ${audioLevel > 0.1 ? 'text-green-500' : 'text-gray-400'}`} />
                <div className="w-48 h-3 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-100 ${
                      isPaused ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(100, audioLevel * 100)}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Main controls */}
            <div className="flex gap-3 justify-center">
              <Button
                variant="outline" size="lg"
                onClick={() => isPaused ? resumeRecording() : pauseRecording()}
              >
                {isPaused ? <><Play className="h-5 w-5 mr-2" />Resume</> : <><Pause className="h-5 w-5 mr-2" />Pause</>}
              </Button>
              <Button
                size="lg"
                onClick={handleStop}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Square className="h-5 w-5 mr-2" />
                Stop & Process
              </Button>
            </div>

            {/* Mid-call Update Ticket button */}
            {ticketId && (
              <div className="space-y-2 pt-2 border-t border-border">
                <Button
                  variant="outline"
                  onClick={handleMidCallUpdate}
                  disabled={midCallIsActive || isPaused}
                  className="w-full gap-2"
                >
                  {midCallIsActive ? (
                    <><Loader2 className="h-4 w-4 animate-spin" />{midCallMessage || 'Updating...'}</>
                  ) : midCallPhase === 'complete' ? (
                    <><Check className="h-4 w-4 text-green-500" />{midCallMessage}</>
                  ) : (
                    <><Upload className="h-4 w-4" />Update Ticket</>
                  )}
                </Button>

                {/* Auto-update interval */}
                <div className="flex items-center justify-center gap-2">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Auto Update:</span>
                  {[null, 5, 15, 30].map((minutes) => (
                    <button
                      key={minutes ?? 'off'}
                      onClick={() => setAutoUpdateMinutes(minutes)}
                      className={`text-xs px-2 py-0.5 rounded ${
                        autoUpdateMinutes === minutes
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      {minutes ? `${minutes}m` : 'Off'}
                    </button>
                  ))}
                </div>

                {/* Update status */}
                {midCallUpdateCount > 0 && (
                  <p className="text-[10px] text-center text-muted-foreground">
                    {midCallUpdateCount} update{midCallUpdateCount !== 1 ? 's' : ''} sent
                    {midCallLastUpdateTime && ` · Last: ${midCallLastUpdateTime.toLocaleTimeString()}`}
                  </p>
                )}

                {midCallPhase === 'error' && midCallMessage && (
                  <p className="text-[10px] text-center text-destructive">{midCallMessage}</p>
                )}
              </div>
            )}

            <Button
              variant="ghost"
              onClick={handleCancel}
              className="w-full text-muted-foreground hover:text-destructive"
            >
              <X className="h-4 w-4 mr-1" /> Cancel Recording
            </Button>
          </div>
        )}

        {/* Processing Phase */}
        {phase === 'processing' && (
          <div className="max-w-md mx-auto space-y-6 text-center">
            <div className="flex flex-col items-center gap-4 py-8">
              <Loader2 className="h-12 w-12 text-primary animate-spin" />
              <div>
                <p className="font-medium text-foreground">{processingLabel}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Recording: {formatDuration(duration)}
                </p>
              </div>
            </div>
            <div className="h-3 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-300"
                style={{ width: `${processingProgress}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground italic">
              This window will update when processing is complete
            </p>
          </div>
        )}

        {/* Notes Phase */}
        {phase === 'notes' && recordingId && (
          <div className="max-w-lg mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <span className="font-medium text-green-400">Recording processed successfully</span>
            </div>

            {ticketId && (
              <div className="flex items-center mb-4 px-3 py-2 bg-blue-500/10 rounded-md text-sm">
                <span className="text-blue-400">Ticket #{ticketId}</span>
              </div>
            )}

            <CallNotesEditor
              key={ticketId || 'no-ticket'}
              recordingId={recordingId}
              ticketId={ticketId}
              onAddedToTicket={handleDone}
            />
            <div className="mt-4 pt-4 border-t">
              <Button variant="outline" onClick={handleDone} className="w-full">
                Close Window
              </Button>
            </div>
          </div>
        )}

        {/* Error state */}
        {status === 'error' && (
          <div className="max-w-md mx-auto text-center space-y-4 py-8">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
            <div>
              <p className="font-medium text-destructive">Something went wrong</p>
              <p className="text-sm text-muted-foreground mt-1">{errorMessage || contextError || 'Unknown error'}</p>
            </div>
            <div className="flex gap-3 justify-center">
              {recordingId ? (
                <Button
                  variant="outline"
                  onClick={handleRetryProcessing}
                  disabled={retrying}
                >
                  {retrying ? <><Loader2 className="h-4 w-4 animate-spin mr-1" />Retrying...</> : 'Retry Processing'}
                </Button>
              ) : (
                <Button variant="outline" onClick={() => { setPhase('select'); setErrorMessage(null) }}>
                  Try Again
                </Button>
              )}
              <Button variant="ghost" onClick={handleCancel}>Close</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
